import { QueryResult, useQuery } from '@apollo/client';

import useCompanyContext from 'common/hooks/useCompanyContext';

import {
  BankTransferDataDocument,
  BankTransferDataQuery as GeneratedBankTransferDataQuery,
} from './graphql/bankTransferData.gql';

type BankTransferDataQuery = Required<GeneratedBankTransferDataQuery>;

type BankTransferDataQueryResult = Omit<
  QueryResult<BankTransferDataQuery>,
  'data'
> & {
  data: Required<BankTransferDataQuery>;
};

type BankTransferDataQueryResultWithoutToBankAccount = Omit<
  QueryResult<BankTransferDataQuery>,
  'data'
> & {
  data: Omit<BankTransferDataQuery, 'toBankAccount'>;
};

type BankTransferDataQueryResultWithoutBankTransferRecipient = Omit<
  QueryResult<BankTransferDataQuery>,
  'data'
> & {
  data: Omit<BankTransferDataQuery, 'viewer'>;
};

type BankTransferDataQueryResultWithoutToBankAccountAndBankTransferRecipient =
  Omit<QueryResult<BankTransferDataQuery>, 'data'> & {
    data: Omit<BankTransferDataQuery, 'toBankAccount' | 'viewer'>;
  };

function useBankTransferData(params: {
  bankTransferRecipientId: string;
  fromBankAccountId: string;
  toBankAccountId: string;
}): BankTransferDataQueryResult;
function useBankTransferData(params: {
  bankTransferRecipientId: string;
  fromBankAccountId: string;
}): BankTransferDataQueryResultWithoutToBankAccount;
function useBankTransferData(params: {
  fromBankAccountId: string;
  toBankAccountId: string;
}): BankTransferDataQueryResultWithoutBankTransferRecipient;
function useBankTransferData(params: {
  fromBankAccountId: string;
}): BankTransferDataQueryResultWithoutToBankAccountAndBankTransferRecipient;
function useBankTransferData({
  bankTransferRecipientId,
  fromBankAccountId,
  toBankAccountId,
}: {
  fromBankAccountId: string;
  toBankAccountId?: string;
  bankTransferRecipientId?: string;
}) {
  const { companyProfileId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error('companyProfileId is required in useBankTransferData');
  }

  return useQuery<BankTransferDataQuery>(BankTransferDataDocument, {
    variables: {
      bankTransferRecipientId: bankTransferRecipientId || '',
      companyProfileId,
      fromBankAccountId,
      toBankAccountId: toBankAccountId || '',
      withBankTransferRecipient: Boolean(bankTransferRecipientId),
      withToBankAccount: Boolean(toBankAccountId),
    },
  }) as
    | BankTransferDataQueryResult
    | BankTransferDataQueryResultWithoutToBankAccount
    | BankTransferDataQueryResultWithoutToBankAccountAndBankTransferRecipient;
}

export default useBankTransferData;
